







































import Vue from 'vue'
import { RawLocation, Route } from 'vue-router'
import { ConflictError } from '@/common/errors'
import { Nullable } from '@/types/utils'
import Loading from '@/components/shared/atoms/Loading'
import WelcomeContainer from '@/containers/Auth/Signup/Welcome'
import AccountFormContainer from '@/containers/Auth/Signup/Form/Account'
import ProfileFormContainer from '@/containers/Profile/Form'
import ProfileConfirmContainer from '@/containers/Profile/Confirm'
import PreCompleteContainer from '@/containers/Auth/Signup/PreComplete'
import SignupUseCase from '@/usecases/auth/SignupUseCase'
import SignupGroupMemberUseCase from '@/usecases/groupMember/SignupGroupMemberUseCase'
import { IProfileSeed } from '@/entities/Profile'

interface IData {
  /**
   * 0. 初めてご利用の方へ
   * 1. アカウント設定フォーム
   * 2. プロフィール設定フォーム
   * 3. 登録内容確認
   * 4. 仮会員登録完了
   */
  step: 0 | 1 | 2 | 3 | 4
  fields: Nullable<Omit<IProfileSeed, 'tlsc'>> // TODO: tlscのみはpropsで渡ってくる
  hasConflictError: boolean
  postSignupGroupMember: boolean
}

export default Vue.extend({
  components: {
    Loading,
    WelcomeContainer,
    AccountFormContainer,
    ProfileFormContainer,
    ProfileConfirmContainer,
    PreCompleteContainer
  },
  props: {
    // Only for dev use like showing stories
    devProps: {
      type: Object as () => {
        step?: IData['step']
      },
      default: null
    },
    presetEmail: {
      type: String,
      default: null
    },
    redirectTo: {
      type: String,
      default: null
    },
    token: {
      type: String,
      default: null
    },
    tlsc: {
      type: String,
      default: null
    },
    forwardedParams: {
      type: Object as () => Route['params'],
      required: true
    },
    forwardedQueries: {
      type: Object as () => Route['query'],
      required: true
    }
  },
  data(): IData {
    return {
      step: 0,
      fields: {
        email: null,
        password: null,
        firstname: null,
        lastname: null,
        firstnameRuby: null,
        lastnameRuby: null,
        birthYear: null,
        birthMonth: null,
        birthDay: null,
        prefecture: null,
        sex: null,
        readFlag: null
      },
      hasConflictError: false,
      postSignupGroupMember: false
    }
  },
  computed: {
    loginPath(): RawLocation {
      return { name: 'login', query: { email: this.presetEmail, tlsc: this.tlsc, redirectTo: this.redirectTo, token: this.token, ...this.forwardedParams, ...this.forwardedQueries } }
    }
  },
  created() {
    // presetEmailが設定されている時はWelcomeをskip
    if (this.presetEmail) {
      this.changeContainer(1, 'account', 'invite')
    }

    // Y!ログインに成功した場合はWelcomeをskip
    if (this.tlsc && this.step < 1) {
      this.changeContainer(1, 'account')
    }

    const devProps = this.devProps
    if (devProps) {
      if (devProps.step) {
        this.step = devProps.step
      }
    }
  },
  destroyed() {
    this.$profileRepository.clearCreateProfileInputField()
    this.$profileRepository.clearSignupMailAddressInputField()
  },
  methods: {
    handleAccountFormChange(params: Pick<IProfileSeed, 'email' | 'password'>) {
      this.fields = {
        ...this.fields,
        ...params
      }
      this.changeContainer(2, 'profile')
      scrollTo(0, 0)
    },
    handleProfileFormChange(params: Omit<IProfileSeed, 'email' | 'password' | 'tlsc'>) {
      this.fields = {
        ...this.fields,
        ...params
      }
      this.changeContainer(3, 'confirm')
      scrollTo(0, 0)
    },
    handleSubmitSuccess() {
      this.hasConflictError = false
      if (this.redirectTo) {
        this.postSignupGroupMember = false
        this.$router.push({ name: this.redirectTo, params: this.forwardedParams, query: this.forwardedQueries })
      } else {
        this.changeContainer(4, 'pre-complete')
        scrollTo(0, 0)
      }
    },
    handleConflictError() {
      this.hasConflictError = true
      this.changeContainer(1, 'conflict')
      scrollTo(0, 0)
    },
    async handleSubmit() {
      const { email, password, firstname, lastname, firstnameRuby, lastnameRuby, birthYear, birthMonth, birthDay, prefecture, sex, readFlag } = this.fields
      const tlsc = this.tlsc

      if (typeof email !== 'string') return
      if (typeof password !== 'string') return
      if (typeof firstname !== 'string') return
      if (typeof lastname !== 'string') return
      if (typeof firstnameRuby !== 'string') return
      if (typeof lastnameRuby !== 'string') return
      if (typeof birthYear !== 'number') return
      if (typeof birthMonth !== 'number') return
      if (typeof birthDay !== 'number') return
      if (typeof prefecture !== 'string') return
      // if (sex !== 'f' && sex !== 'm') return
      if (typeof readFlag !== 'boolean') return

      const seed: IProfileSeed = {
        email,
        password,
        firstname,
        lastname,
        firstnameRuby,
        lastnameRuby,
        birthYear,
        birthMonth,
        birthDay,
        prefecture,
        sex,
        readFlag,
        tlsc
      }

      // 招待tokenがある場合はファミリーメンバー登録
      if (this.token) {
        this.postSignupGroupMember = true
        await this.signupGroupMember(seed)
      } else {
        await this.signup(seed)
      }
    },
    async signup(seed: IProfileSeed) {
      const usecase = new SignupUseCase({
        snapsnapGateway: this.$snapsnapGateway
      })

      try {
        await usecase.execute(seed)
        this.handleSubmitSuccess()
        this.$profileRepository.clearCreateProfileInputField()
      } catch (e) {
        if (e instanceof ConflictError) {
          this.handleConflictError()
        }
        console.log(e)
        // TODO: Handle error
      }
    },
    async signupGroupMember(seed: IProfileSeed) {
      const usecase = new SignupGroupMemberUseCase({
        snapsnapGateway: this.$snapsnapGateway,
        profileRepository: this.$profileRepository
      })

      try {
        await usecase.execute({
          seed,
          token: this.token
        })
        this.handleSubmitSuccess()
        this.$profileRepository.clearCreateProfileInputField()
      } catch (e) {
        if (e instanceof ConflictError) {
          this.postSignupGroupMember = false
          this.handleConflictError()
        }
        console.log(e)
      }
    },
    changeContainer(stepNumber: 1 | 2 | 3 | 4, signupProcessName: 'account' | 'profile' | 'confirm' | 'pre-complete' | 'conflict', mode?: 'invite') {
      // Containerが切り替わるごとに$router.replaceでquery parameterを設定 (GA側で区別するため)
      this.step = stepNumber
      const route = this.$route
      this.$router.replace({
        ...route,
        query: {
          ...route.query,
          signupProcessName
        }
      })
      if (mode !== undefined) {
        this.$router.replace({
          ...route,
          query: {
            ...route.query,
            mode
          }
        })
      }
    }
  }
})
